import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [articles, setArticles] = useState([]);
  const [entities, setEntities] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [language, setLanguage] = useState('en');
  const [showArchive, setShowArchive] = useState(false);
  const [activeView, setActiveView] = useState('news'); // news, people, places, companies
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ active: 0, archived: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (activeView === 'news') {
      fetchArticles();
      fetchStats();
    } else {
      fetchEntities(activeView);
    }
  }, [language, showArchive, currentPage, activeView]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const archived = showArchive ? '1' : '0';
      const response = await fetch(
        `/api/articles?lang=${language}&archived=${archived}&page=${currentPage}&limit=10`
      );
      const data = await response.json();
      setArticles(data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEntities = async (category) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/entities/${category}?lang=${language}&page=${currentPage}&limit=20`);
      const data = await response.json();
      setEntities(data.entities || []);
    } catch (error) {
      console.error('Error fetching entities:', error);
      setEntities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntityDetails = async (category, id) => {
    try {
      const response = await fetch(`/api/entities/${category}/${id}?lang=${language}`);
      const data = await response.json();
      setSelectedEntity(data);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error fetching entity:', error);
    }
  };

  const fetchArticleDetails = async (id) => {
    try {
      const response = await fetch(`/api/articles/${id}?lang=${language}`);
      const data = await response.json();
      setSelectedArticle(data);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error fetching article:', error);
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'pt-BR' : 'en');
  };

  const translations = {
    en: {
      home: 'News',
      people: 'People',
      places: 'Places',
      companies: 'Companies',
      archive: 'Archive',
      readMore: 'Read More',
      learnMore: 'Learn More',
      backToList: 'Back to List',
      loading: 'Loading...',
      noArticles: 'No articles found',
      noEntities: 'No entries found',
      activeStories: 'Active Stories',
      archivedStories: 'Archived Stories',
      tagline: 'Your source for all things Titton',
      previousPage: 'Previous',
      nextPage: 'Next'
    },
    'pt-BR': {
      home: 'Notícias',
      people: 'Pessoas',
      places: 'Lugares',
      companies: 'Empresas',
      archive: 'Arquivo',
      readMore: 'Leia Mais',
      learnMore: 'Saiba Mais',
      backToList: 'Voltar à Lista',
      loading: 'Carregando...',
      noArticles: 'Nenhum artigo encontrado',
      noEntities: 'Nenhuma entrada encontrada',
      activeStories: 'Histórias Ativas',
      archivedStories: 'Histórias Arquivadas',
      tagline: 'Sua fonte para tudo sobre Titton',
      previousPage: 'Anterior',
      nextPage: 'Próximo'
    }
  };

  const t = translations[language];

  // Entity detail view
  if (selectedEntity) {
    return (
      <div className="App">
        <header className="app-header">
          <div className="header-content">
            <h1 className="site-title">TITTON</h1>
            <p className="tagline">{t.tagline}</p>
            <nav className="main-nav">
              <button onClick={() => setSelectedEntity(null)} className="nav-link">
                {t.backToList}
              </button>
              <button onClick={toggleLanguage} className="language-toggle">
                {language === 'en' ? '🇧🇷 PT' : '🇺🇸 EN'}
              </button>
            </nav>
          </div>
        </header>

        <main className="main-content">
          <article className="article-detail">
            <button onClick={() => setSelectedEntity(null)} className="back-button">
              ← {t.backToList}
            </button>
            
            {selectedEntity.image_url && (
              <img 
                src={selectedEntity.image_url} 
                alt={selectedEntity.name}
                className="article-hero-image"
              />
            )}
            
            <h1 className="article-title">{selectedEntity.name}</h1>
            
            <div className="article-meta">
              <span className="entity-category">{selectedEntity.category.toUpperCase()}</span>
            </div>
            
            <div className="article-body">
              <p><strong>{selectedEntity.description}</strong></p>
              <div dangerouslySetInnerHTML={{ __html: selectedEntity.content.split('\n\n').map(p => `<p>${p}</p>`).join('') }} />
              
              {selectedEntity.source_url && (
                <p style={{ marginTop: '30px', fontStyle: 'italic' }}>
                  <strong>Source:</strong> <a href={selectedEntity.source_url} target="_blank" rel="noopener noreferrer">Learn more →</a>
                </p>
              )}
            </div>
          </article>
        </main>

        <footer className="app-footer">
          <p>© {new Date().getFullYear()} Titton.com - All rights reserved</p>
        </footer>
      </div>
    );
  }

  if (selectedArticle) {
    return (
      <div className="App">
        <header className="app-header">
          <div className="header-content">
            <h1 className="site-title">TITTON</h1>
            <p className="tagline">{t.tagline}</p>
            <nav className="main-nav">
              <button onClick={() => setSelectedArticle(null)} className="nav-link">
                {t.home}
              </button>
              <button onClick={() => setShowArchive(!showArchive)} className="nav-link">
                {t.archive}
              </button>
              <button onClick={toggleLanguage} className="language-toggle">
                {language === 'en' ? '🇧🇷 PT' : '🇺🇸 EN'}
              </button>
            </nav>
          </div>
        </header>

        <main className="main-content">
          <article className="article-detail">
            <button onClick={() => setSelectedArticle(null)} className="back-button">
              ← {t.backToList}
            </button>
            
            {selectedArticle.image_url && (
              <img 
                src={selectedArticle.image_url} 
                alt={selectedArticle.title}
                className="article-hero-image"
              />
            )}
            
            <h1 className="article-title">{selectedArticle.title}</h1>
            
            <div className="article-meta">
              {new Date(selectedArticle.created_at).toLocaleDateString(
                language === 'pt-BR' ? 'pt-BR' : 'en-US',
                { year: 'numeric', month: 'long', day: 'numeric' }
              )}
            </div>
            
            <div 
              className="article-body"
              dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
            />
          </article>

          {/* Ad Space */}
          <div className="ad-container">
            <div className="ad-placeholder">
              Advertisement Space
            </div>
          </div>
        </main>

        <footer className="app-footer">
          <p>© {new Date().getFullYear()} Titton.com - All rights reserved</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <h1 className="site-title">TITTON</h1>
          <p className="tagline">{t.tagline}</p>
          <nav className="main-nav">
            <button 
              onClick={() => { setActiveView('news'); setShowArchive(false); setCurrentPage(1); setSelectedEntity(null); }} 
              className={`nav-link ${activeView === 'news' ? 'active' : ''}`}
            >
              {t.home}
            </button>
            <button 
              onClick={() => { setActiveView('people'); setCurrentPage(1); setSelectedArticle(null); setSelectedEntity(null); }} 
              className={`nav-link ${activeView === 'people' ? 'active' : ''}`}
            >
              {t.people}
            </button>
            <button 
              onClick={() => { setActiveView('places'); setCurrentPage(1); setSelectedArticle(null); setSelectedEntity(null); }} 
              className={`nav-link ${activeView === 'places' ? 'active' : ''}`}
            >
              {t.places}
            </button>
            <button 
              onClick={() => { setActiveView('companies'); setCurrentPage(1); setSelectedArticle(null); setSelectedEntity(null); }} 
              className={`nav-link ${activeView === 'companies' ? 'active' : ''}`}
            >
              {t.companies}
            </button>
            <button onClick={toggleLanguage} className="language-toggle">
              {language === 'en' ? '🇧🇷 PT' : '🇺🇸 EN'}
            </button>
          </nav>
        </div>
      </header>

      <main className="main-content">
        <div className="content-grid">
          <div className="articles-section">
            {loading ? (
              <div className="loading">{t.loading}</div>
            ) : activeView === 'news' ? (
              articles.length === 0 ? (
                <div className="no-articles">{t.noArticles}</div>
              ) : (
                <>
                  {articles.map((article) => (
                  <article key={article.id} className="article-card">
                    {article.image_url && (
                      <img 
                        src={article.image_url} 
                        alt={article.title}
                        className="article-thumbnail"
                      />
                    )}
                    <div className="article-content">
                      <h2 className="article-card-title">{article.title}</h2>
                      <p className="article-summary">{article.summary}</p>
                      <div className="article-footer">
                        <span className="article-date">
                          {new Date(article.created_at).toLocaleDateString(
                            language === 'pt-BR' ? 'pt-BR' : 'en-US'
                          )}
                        </span>
                        <button 
                          onClick={() => fetchArticleDetails(article.id)}
                          className="read-more-btn"
                        >
                          {t.readMore} →
                        </button>
                      </div>
                    </div>
                  </article>
                ))}

                <div className="pagination">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    {t.previousPage}
                  </button>
                  <span className="page-number">Page {currentPage}</span>
                  <button 
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={articles.length < 10}
                    className="pagination-btn"
                  >
                    {t.nextPage}
                  </button>
                </div>
              </>
              )
            ) : (
              // Entity view (people, places, companies)
              entities.length === 0 ? (
                <div className="no-articles">{t.noEntities}</div>
              ) : (
                <>
                  {entities.map((entity) => (
                    <article key={entity.id} className="article-card">
                      {entity.image_url && (
                        <img 
                          src={entity.image_url} 
                          alt={entity.name}
                          className="article-thumbnail"
                        />
                      )}
                      <div className="article-content">
                        <h2 className="article-card-title">{entity.name}</h2>
                        <p className="article-summary">{entity.description}</p>
                        <div className="article-footer">
                          <span className="article-date entity-category">
                            {entity.category.toUpperCase()}
                          </span>
                          <button 
                            onClick={() => fetchEntityDetails(activeView, entity.id)}
                            className="read-more-btn"
                          >
                            {t.learnMore} →
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}

                  <div className="pagination">
                    <button 
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      {t.previousPage}
                    </button>
                    <span className="page-number">Page {currentPage}</span>
                    <button 
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={entities.length < 20}
                      className="pagination-btn"
                    >
                      {t.nextPage}
                    </button>
                  </div>
                </>
              )
            )}
          </div>

          <aside className="sidebar">
            <div className="sidebar-widget">
              <h3>{showArchive ? t.archivedStories : t.activeStories}</h3>
              <div className="stats">
                <div className="stat-item">
                  <span className="stat-number">{stats.active}</span>
                  <span className="stat-label">{t.activeStories}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{stats.archived}</span>
                  <span className="stat-label">{t.archivedStories}</span>
                </div>
              </div>
            </div>

            {/* Ad Space */}
            <div className="ad-container sidebar-ad">
              <div className="ad-placeholder">
                Advertisement Space
              </div>
            </div>
          </aside>
        </div>
      </main>

      <footer className="app-footer">
        <p>© {new Date().getFullYear()} Titton.com - All rights reserved</p>
      </footer>
    </div>
  );
}

export default App;
